// Terminal display helpers — name, location, icon, ID.
//
// These are the "single source of truth" for how a terminal appears in
// the UI. They normalize away:
//   - missing/empty fields (return a sensible placeholder)
//   - the "the operator named the terminal after its ID" edge case
//     (we treat that as "no name set" so the placeholder shows)
//
// Lifted from admin/index.php's `function displayTerminalX(t)` set.

import type { Terminal } from '../types';
import { t } from '../i18n';

function isEmptyName(raw: string, terminal: Terminal, field: 'name' | 'location' | 'id'): boolean {
  const idAsString = String(terminal.mid || '').trim();
  if (field === 'id') {
    return !raw || raw === idAsString;
  }
  return !raw || raw === idAsString;
}

export function displayTerminalName(terminal: Terminal | null | undefined): string {
  const fallback = t('common.unnamed');
  if (!terminal) return fallback;
  const raw = String(terminal.name || '').trim();
  if (isEmptyName(raw, terminal, 'name')) return fallback;
  return raw;
}

export function displayTerminalLocation(terminal: Terminal | null | undefined): string {
  const fallback = '{location}';
  if (!terminal) return fallback;
  const raw = String(terminal.location || '').trim();
  if (isEmptyName(raw, terminal, 'location')) return fallback;
  return raw;
}

export function displayTerminalID(terminal: Terminal | null | undefined): string {
  const fallback = '#0000';
  if (!terminal) return fallback;
  const raw = String(terminal.id || '').trim();
  if (isEmptyName(raw, terminal, 'id')) return fallback;
  return raw;
}

// The icon is currently a constant — Timestop's branding is single-icon
// everywhere. Kept as a function so future per-terminal icons don't
// require touching call sites.
export function displayTerminalIcon(_t: Terminal | null | undefined): string {
  return '🏷️';
}

// Tiny status pill helper used by the dashboard cards.
//
// Note: `term.status === 'online' || term.status === 'Online'` is a
// status comparison (the spot terminal can write either casing
// depending on firmware). We compare against the stable enum string
// and translate the title only. The enum itself stays English because
// future `term.status === 'X'` checks would otherwise need
// translation-aware comparisons.
export function quickStatusIcons(term: Terminal): string {
  const online = term.status === 'online' || term.status === 'Online';
  const icon = online ? '🟢' : '⚫';
  return `<span title="${online ? t('status.online') : t('status.offline')}">${icon}</span>`;
}
